import { Icon, InfoLabel } from '@jutro/components';
import { IntlContext, useTranslator } from '@jutro/locale';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { getNormalizedLOBName } from '@xengage/gw-portals-config-js';
import { LobIconUtil, DatatableUtil } from '@xengage/gw-portals-util-js';
import appConfig from 'app-config';
import { ClaimService } from 'gw-capability-claim';
import { ViewModelServiceContext } from "@xengage/gw-portals-viewmodel-react";
import { ClaimLobOptionsMenu, VendorLinkComponent } from 'gw-capability-claim-react';
import _ from 'lodash';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import claimsMessages from 'gw-capability-claim-react/Claims.messages';
import { Link } from '@jutro/router';
import { CurrencyField } from '@jutro/legacy/components';
import styles from '../../pages/ClaimList/ClaimList.module.scss';
import metadata from './ClaimListComponent.metadata.json5';
import messages from './ClaimListComponent.messages';


function ClaimListComponent({ history }) {
    const authData = useAuthentication()
    const authHeader = _.get(authData, 'authHeader');
    const authUserData = _.get(authData, 'authUserData');
    const isTFPA = authUserData?.associationName_TWIA === 'tfpa';
    const noAuthorizations = authData?.noCurrentAuthorizations_TWIA;
    const { vendorTypesForPolicyHolderByLob } =
        appConfig;
    const [claimDataTable, setClaimDataTable] = useState('');
    const [isLoading, setLoadingState] = useState(false);
    const translator = useTranslator();
    const intl = useContext(IntlContext);
    const viewModelService = useContext(ViewModelServiceContext)
    const [searchByReportedDateExt, setSearchByReportedDateExt] = useState(false);
    const [searchByLossDateExt, setSearchByLossDateExt] = useState(false);
    const [fromDateExt, setFromDateExt] = useState(undefined);
    const [toDateExt, setToDateExt] = useState(undefined);
    const [claimQuery, setClaimQuery] = useState('');
    const [showErrors, setShowErrors] = useState(false);
    const [showClosed, setShowClosed] = useState(true);
    const [showDateValidation, setShowDateValidation] = useState(false);
    const [pageConfig, setPageConfig] = useState({
        page: 0,
        pageSize: 25
    });


    const getVendors = useCallback(
        claim => {
            if (!claim.vendors) {
                return [];
            }

            const normalizedLobCode = getNormalizedLOBName(
                claim.lineOfBusinessCode
            );
            const usedVendorTypes =
                vendorTypesForPolicyHolderByLob[normalizedLobCode] || [];

            const policyHolderVendors = claim.vendors.filter(vendor => {
                let type = vendor.contactDTO.subtype;

                if (normalizedLobCode === 'ho') {
                    type = vendor.contactDTO.contactType;
                }

                return usedVendorTypes.indexOf(type) >= 0;
            });

            return policyHolderVendors;
        },
        [vendorTypesForPolicyHolderByLob]
    );

    const processClaimsData = useCallback(
        claimsData => {
            if (!_.isEmpty(claimsData)) {
                let claimsArrayResult = claimsData;

                claimsArrayResult = claimsArrayResult.map(claimInfo => {
                    const claims = {
                        product: claimInfo.lineOfBusinessCode,
                        accountName: claimInfo.accountName,
                        lossLocation_TWIA: claimInfo.lossLocation_TWIA,
                        claimNumber: claimInfo.claimNumber,
                        reportedDateExt: intl.formatDate(
                            new Date(claimInfo.reportedDateExt),
                            { year: 'numeric', month: 'short', day: 'numeric' }
                        ),
                        paid: claimInfo.indemnityPayments_TFPA,
                        dateOfLoss: intl.formatDate(
                            new Date(claimInfo.lossDate),
                            { year: 'numeric', month: 'short', day: 'numeric' }
                        ),
                        status: {
                            id: `typekey.ClaimState.${claimInfo.claimState}`,
                            defaultMessage: claimInfo.claimState,
                        },
                        policyNumber: claimInfo.policyNumber,
                        vendors: getVendors(claimInfo),
                        isTWIAPC9: claimInfo.isTWIAPC9_TWIA,
                    };

                    return claims;
                });

                return claimsArrayResult;
            }

            return [];
        },
        [getVendors, intl]
    );

    const getClaims = useCallback(
        query => {
            const claimsPromise = ClaimService.getClaimSummaries(
                query,
                {},
                authHeader
            );

            return claimsPromise
                .then(responseData => {
                    const data = processClaimsData(responseData.items);                    

                    setClaimDataTable(data);

                    const newPageConfig = _.clone(pageConfig)

                    if (data.length <= 25) {
                        newPageConfig.page = 0
                    }

                    setPageConfig(newPageConfig)
                })
                .finally(() => {
                    setLoadingState(false);
                });
        },
        [authHeader, pageConfig, processClaimsData]
    );

    const localDateFromDate = (localDate, toDate) => {
        if (localDate && localDate.year) {
            const dateTime = new Date();

            if(toDate){
                dateTime.setHours(23, 59, 0, 0);
            } else {                
                dateTime.setHours(0, 0, 0, 0);
            }
            
            dateTime.setFullYear(localDate.year);
            dateTime.setMonth(localDate.month);
            dateTime.setDate(localDate.day);

            return dateTime;
        }

        return null;
    };

    const loadClaimData = useCallback(
        () => {
            setLoadingState(true);

            const query = {
                claimStates: showClosed ? ['open', 'closed'] : ['open'],
                fromDateExt: localDateFromDate(fromDateExt),
                searchByLossDateExt,
                searchByReportedDateExt,
                states: showClosed ? ['open', 'closed'] : ['open'],
                toDateExt: localDateFromDate(toDateExt, true),
                queryText: claimQuery
            }

            return getClaims(query);
        },
        [claimQuery, fromDateExt, getClaims, searchByLossDateExt, searchByReportedDateExt, showClosed, toDateExt]
    );

    useEffect(() => {
        if(authHeader){
            loadClaimData();
        }
        

        // Disabling to prevent continues re-rendering
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authHeader]);


    const getCell = useCallback(
        (item, index, property) => {
            const toolTipMessage = {
                product: translator(claimsMessages.line),
                dateOfLoss: translator(claimsMessages.dateOfLossClaimDetail),
            };

            return (
                <span title={toolTipMessage[property.id]}>
                    {item[property.id]}
                </span>
            );
        },
        [translator]
    );

    const getStatusCell = useCallback(
        (item, index, property) => {
            const titleMessage = translator(claimsMessages.claimStatus);
            const status = item[property.id];
            let typeStatus = 'success';

            if (
                status ===
                translator({
                    id: 'typekey.ClaimState.draft',
                    defaultMessage: 'draft',
                })
            ) {
                typeStatus = 'warning';
            } else if (
                status ===
                translator({
                    id: 'typekey.ClaimState.closed',
                    defaultMessage: 'closed',
                })
            ) {
                typeStatus = 'neutral';
            }

            return (
                <InfoLabel
                    type={typeStatus}
                    size="small"
                    title={titleMessage[property.id]}
                >
                    {translator(item[property.id])}
                </InfoLabel>
            );
        },
        [translator]
    );

    const getVendorsCell = useCallback((item, index, property) => {
        const vendors = item[property.id];

        return <VendorLinkComponent id="vendorsCell" vendors={vendors} />;
    }, []);

    const getLink = useCallback(
        (item, index, property) => {
            const toolTipMessage = {
                policyNumber: translator(
                    claimsMessages.policyNumberClaimDetail
                ),
                claimNumber: translator(claimsMessages.claimNumber),
            };

            if (property.id === 'claimNumber') {
                const redirectpath = {
                    pathname: `/claims/${item[property.id]}`,
                    state: {
                        claimNumber: item[property.id],
                        redirectPath: '/claims',
                        claimStatus: item.status,
                    },
                };

                return (
                    <Link
                        to={redirectpath}
                        className={styles.gwClaimsLinkContent}
                        title={toolTipMessage[property.id]}
                    >
                        {item[property.id]}
                    </Link>
                );
            }

            const redirectPath = {
                pathname: `/account-policy-details/${item.policyNumber}`,
                state: {
                    claimNumber: item[property.id],
                    redirectPath: '/claims',
                    claimStatus: item.status,
                },
            };

            return (
                /*
                    DE18716 - Cloud Portals_Regression_CE: Policy# is in the form of hyperlink and upon clicking it Sorry an error occurred is displayed
                    Policy Number of type "123456789-01" is not accessible to users
                    DE19004 - Hide TFPA policy link
                */
                item.isTWIAPC9 ? 
                <Link
                    to={redirectPath}
                    className={styles.gwClaimsLinkContent}
                    title={toolTipMessage[property.id]}
                >{item[property.id]}</Link> :
                <span>{item[property.id]}</span>
            );
        },
        [translator]
    );

    const getProductIcon = useCallback(item => {
        const icon = LobIconUtil.getMaterialIcon(item.product);

        return <Icon icon={icon} title={item.product} key={item.product} />;
    }, []);

    const getCurrencyCell = (item, index, { id: property }) => {
        if(item[property].amount === 0) {
            return (
                <span>
                    <CurrencyField
                        id={`claimsCurrency_${index}`}
                        value="-"
                        datatype="string"
                        readOnly
                        hideLabel
                    />
                </span>
            );
        }

        return (
            <span>
                <CurrencyField
                    id={`claimsCurrency_${index}`}
                    value={item[property]}
                    datatype="object"
                    readOnly
                    hideLabel
                />
            </span>
        );
    };

    const handleFileClick = useCallback(
        () =>
            history.push({
                pathname: '/fnol-select-policy',
                state: {
                    redirectPath: '/claims',
                },
            }),
        [history]
    );

    const validateClaimSearch = (reportDate, lossDate, fromDate, toDate) => {


        if ((reportDate || lossDate) && (fromDate === undefined || toDate === undefined)) {
            setShowErrors(true);

            return false;
        }

        const dto = viewModelService.create({}, 'cc', 'edge.capabilities.claim.dto.ClaimSearchDTO');

        dto.searchByReportedDateExt.value = reportDate;
        dto.searchByLossDateExt.value = lossDate;
        dto.queryText.value = claimQuery;

        if (fromDate !== undefined) {
            dto.fromDateExt.value = localDateFromDate(fromDate);
        }

        if (toDate !== undefined) {
            dto.toDateExt.value = localDateFromDate(toDate);
        }

        const isDataValid = dto.aspects.valid && dto.aspects.subtreeValid;


        if (!isDataValid) {
            setShowDateValidation(true);

            return false;
        }

        setShowErrors(false);
        setShowDateValidation(false);

        return true;
    }

    const handleSearchClaimsFilter = (value, path) => {
        switch (path) {
            case 'searchByReportedDateExt':
                setSearchByReportedDateExt(value);
                validateClaimSearch(value, searchByLossDateExt, fromDateExt, toDateExt);
                break;
            case 'searchByLossDateExt':
                setSearchByLossDateExt(value);
                validateClaimSearch(searchByReportedDateExt, value, fromDateExt, toDateExt);
                break;
            case 'showClosed':
                setShowClosed(value);
                break;
            case 'fromDateExt':
                setFromDateExt(value);
                validateClaimSearch(searchByReportedDateExt, searchByLossDateExt, value, toDateExt);
                break;
            case 'toDateExt':
                setToDateExt(value);
                validateClaimSearch(searchByReportedDateExt, searchByLossDateExt, fromDateExt, value);
                break;
            case 'claimQuery':
                setClaimQuery(value);
                break;
            default:
        }
        
    };

    const overrides = {
        claimListTableLoader: {
            loaded: !isLoading,
        },
        claimTable: {
            data: claimDataTable,
            visible: !isLoading,
            config: pageConfig,
            onConfigChange: (data) => {
                const newPageConfig = _.clone(data)

                if (claimDataTable.length <= 25) {
                    newPageConfig.page = 0
                }

                setPageConfig(newPageConfig)
            }
        },
        noDataMessage: {
            visible: _.isEmpty(claimDataTable) && !isLoading,
            content: translator(claimsMessages.noClaimsResult),
        },
        fileClaimButton: {
            to: handleFileClick,
            visible: !noAuthorizations,
        },
        claimSearch: {
            value: claimQuery
        },
        searchByReportedDate: {
            value: searchByReportedDateExt
        },
        searchByLossDate: {
            value: searchByLossDateExt
        },
        fromDate: {
            value: fromDateExt,
            required: searchByReportedDateExt || searchByLossDateExt,
            showRequired: searchByReportedDateExt || searchByLossDateExt,
            showErrors
        },
        toDate: {
            value: toDateExt,
            required: searchByReportedDateExt || searchByLossDateExt,
            showRequired: searchByReportedDateExt || searchByLossDateExt,
            showErrors
        },
        closedClaims: {
            value: showClosed,
            visible: isTFPA
        },
        toDateGreaterThanFromDate:{
            visible: showDateValidation,
            message: translator(messages.toDateGreaterThanFromDate)
        },
        searchClaimBtn: {
            disabled: showErrors || showDateValidation
        },
        addressColumn:{
            visible: !isTFPA
        },
        mailingAddressColumn:{
            visible: isTFPA
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            getCell,
            getLink,
            getProductIcon,
            getVendorsCell,
            getStatusCell,
            handleFileClick,
            handleSearchClaimsFilter,
            searchClaims: loadClaimData,
            getCurrencyCell,
            sortDate: DatatableUtil.sortDate,
        },
        resolveComponentMap: {
            claimloboptionsmenu: ClaimLobOptionsMenu,
        },
    };

    return (
        <MetadataContent
            uiProps={metadata.componentContent}
            overrideProps={overrides}
            {...resolvers}
        />
    );
}

export default withRouter(ClaimListComponent);
